import React, { useState } from "react";
import { Plus, Minus, Car, Users } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { TRANSFERS } from "../mock";
import SectionHeader from "./SectionHeader";
import useReveal from "../hooks/useReveal";
import { Button } from "./ui/button";

export default function Transfers() {
  const { t, S, lang } = useLang();
  const { addItem } = useCart();
  const ref = useReveal();

  // persons per transfer row
  const [persons, setPersons] = useState(() =>
    TRANSFERS.reduce((acc, tr) => ({ ...acc, [tr.id]: 2 }), {})
  );

  const setP = (id, val) => setPersons((p) => ({ ...p, [id]: Math.max(1, Math.min(20, val)) }));

  const add = (tr) => {
    const qty = persons[tr.id] || 1;
    addItem(
      {
        id: tr.id,
        type: "transfer",
        name: tr.route,
        subtitle: tr.vehicle,
        price: tr.price,
        img: tr.img,
      },
      qty
    );
    toast.success(t(S.added), { description: `${tr.route[lang]} · ${qty} ${t(S.transfers.persons).toLowerCase()}` });
  };

  return (
    <section id="tarifas" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <SectionHeader kicker={t(S.transfers.kicker)} title={t(S.transfers.title)} sub={t(S.transfers.sub)} />

        <div className="mt-12 reveal rounded-2xl border border-border overflow-hidden shadow-sm">
          {/* header row */}
          <div className="hidden lg:grid grid-cols-12 gap-3 bg-primary text-primary-foreground px-6 py-4 text-sm font-semibold uppercase tracking-wide">
            <div className="col-span-4">{t(S.transfers.colRoute)}</div>
            <div className="col-span-2">{t(S.transfers.colVehicle)}</div>
            <div className="col-span-2 text-right">{t(S.transfers.colPrice)}</div>
            <div className="col-span-2 text-center">{t(S.transfers.persons)}</div>
            <div className="col-span-2" />
          </div>

          {TRANSFERS.map((tr, i) => {
            const p = persons[tr.id] || 1;
            return (
              <div
                key={tr.id}
                className={`grid grid-cols-1 lg:grid-cols-12 items-center gap-4 px-6 py-5 ${i % 2 ? "bg-secondary/40" : "bg-white"}`}
              >
                <div className="lg:col-span-4 flex items-center gap-3">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
                    <Car className="w-4.5 h-4.5" />
                  </span>
                  <span className="font-medium text-foreground">{t(tr.route)}</span>
                </div>

                <div className="lg:col-span-2 text-muted-foreground">{t(tr.vehicle)}</div>

                <div className="lg:col-span-2 lg:text-right">
                  <span className="font-display font-bold text-lg text-primary">
                    ${tr.price}
                    <span className="text-xs text-muted-foreground font-sans font-medium ml-0.5">USD</span>
                  </span>
                  <span className="block text-[11px] text-muted-foreground">{t(S.transfers.perPerson)}</span>
                </div>

                {/* persons stepper */}
                <div className="lg:col-span-2 lg:justify-self-center">
                  <div className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-1 py-1">
                    <button
                      onClick={() => setP(tr.id, p - 1)}
                      className="grid place-items-center w-7 h-7 rounded-full hover:bg-secondary text-primary"
                      aria-label="menos"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-semibold flex items-center justify-center gap-1">
                      <Users className="w-3.5 h-3.5 text-muted-foreground" />
                      {p}
                    </span>
                    <button
                      onClick={() => setP(tr.id, p + 1)}
                      className="grid place-items-center w-7 h-7 rounded-full hover:bg-secondary text-primary"
                      aria-label="más"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-2 lg:text-right">
                  <Button
                    onClick={() => add(tr)}
                    size="sm"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full w-full lg:w-auto"
                  >
                    <Plus className="w-4 h-4" />
                    {t(S.transfers.add)} · ${tr.price * p}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-5 text-sm text-muted-foreground reveal">{t(S.transfers.note)}</p>
      </div>
    </section>
  );
}
